import * as React from 'react';
import { FieldWrapper } from '@progress/kendo-react-form';
import BaseInput from '../inputs/BaseInput';
import { FloatingLabel, Error, Hint } from '@progress/kendo-react-labels';
import cn from 'classnames';
// eslint-disable-next-line no-unused-vars
import BaseFormControl from './BaseFormControl';

/**
 * @typedef {React.ComponentProps<BaseFormControl>} BaseFieldRenderProps
 * @typedef {React.ComponentProps<BaseInput>} BaseInputProps
 *
 * @param {BaseFieldRenderProps & BaseInputProps} fieldRenderProps
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement}
 */
const FormInput = (fieldRenderProps, ref) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    name,
    valid,
    disabled,
    hint,
    type,
    optional,
    readOnly,
    inputComponent = BaseInput,
    valueRenderer,
    value,
    wrapperClassName,
    placeholder,
    stopWheel = true,
    ...rest
  } = fieldRenderProps;

  const editorId = id ?? name;

  const showValidationMessage = touched && validationMessage;
  const showHint = !showValidationMessage && hint;
  const hintId = showHint ? `${editorId}_hint` : '';
  const errorId = showValidationMessage ? `${editorId}_error` : '';
  const labelId = label ? `${editorId}_label` : '';
  const InputComponent = inputComponent;

  const handleStopWheel = (event) => {
    event?.stopPropagation();
  };

  const innerRef = React.useRef(null);
  React.useImperativeHandle(ref, () => innerRef?.current);
  React.useEffect(() => {
    if (stopWheel) {
      innerRef.current?.element?.addEventListener('wheel', handleStopWheel);
    } else {
      innerRef.current?.element?.removeEventListener('wheel', handleStopWheel);
    }
    return () => {
      innerRef.current?.element?.removeEventListener('wheel', handleStopWheel);
    };
  }, [stopWheel]);

  if (!label) {
    return (
      <FieldWrapper className={cn(wrapperClassName, readOnly ? 'form-input-readonly' : '')}>
        <InputComponent
          valid={valid}
          type={type}
          id={editorId}
          name={name}
          disabled={disabled}
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          readOnly={readOnly}
          value={valueRenderer ? valueRenderer(value) : value}
          placeholder={placeholder}
          ref={innerRef}
          dataTestId={editorId}
          {...rest}
        />
        {showHint && <Hint id={hintId}>{hint}</Hint>}
        {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
      </FieldWrapper>
    );
  }

  return (
    <FieldWrapper className={cn(wrapperClassName, readOnly ? 'form-input-readonly' : '')}>
      <FloatingLabel
        label={label}
        id={labelId}
        optional={optional}
        editorId={editorId}
        editorValid={valid}
        editorDisabled={disabled}
        editorValue={value}
        editorPlaceholder={placeholder}
        {...(readOnly ? { className: 'label-readonly' } : {})}
      >
        <InputComponent
          valid={valid}
          type={type}
          id={editorId}
          name={name}
          disabled={disabled}
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          readOnly={readOnly}
          value={valueRenderer ? valueRenderer(value) : value}
          placeholder={placeholder}
          ref={innerRef}
          dataTestId={editorId}
          {...rest}
        />
      </FloatingLabel>
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  );
};

/**
 *  An input form field.
 *
 * @param {BaseFieldRenderProps & BaseInputProps} props Component props. Extends BaseFieldRender and BaseInput props.
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement} Input form field.
 */
const ForwardFormInput = React.forwardRef(FormInput);

export default ForwardFormInput;
